import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";

import {
    ProductVariation,
    ProductVariationSearchFilterOptions
} from "@shopliftr/common-js/admin";
import {
    BulkResponse,
    BulkUtil,
    RequestUtil,
    SearchRequest,
    SearchResponse
} from "@shopliftr/common-js/shared";

import {
    IReadOnlyInternalApiClientService,
    InternalApiRequestService,
    IQueryInternalApiClientService,
    ListUtil
} from "@shopliftr/common-ng";
import {
    ModelAction, ISearchSort
} from "@shopliftr/common-js/data";

/**
 * HTTP Request Service for managing product variations via Internal API.
 *
 * This service implements the **Repository Pattern** (API Client Service), providing typed
 * HTTP operations for ProductVariation resources. It encapsulates the API endpoint path,
 * builds typed requests using RequestUtil, and maps responses to domain models.
 *
 * @pattern Repository Pattern / HTTP Request Service
 * @providedIn root
 * @implements {IReadOnlyInternalApiClientService<ProductVariation>}
 * @implements {IQueryInternalApiClientService<ProductVariation>}
 *
 * @see {@link IReadOnlyInternalApiClientService}
 * @see {@link IQueryInternalApiClientService}
 * @see {@link ProductVariation}
 */
@Injectable(
    { providedIn: "root" }
)
export class ProductVariationApiClientService implements IReadOnlyInternalApiClientService<ProductVariation>,
IQueryInternalApiClientService<ProductVariation> {
    /**
     * Base API endpoint path for product variation operations.
     * Used to construct full URLs for HTTP requests.
     */
    readonly basePath = "product-variation";

    /**
     * Internal API request service for making HTTP calls to the backend.
     * Handles request preprocessing, URL generation, and error handling.
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Performs a paginated search for ProductVariation entities matching the specified criteria.
     * Returns results with pagination metadata for efficient data loading.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [page] - Zero-based page number for pagination
     * @param [pageSize] - Number of results per page
     * @param [sort] - Optional sorting configuration
     * @returns Observable that emits a SearchResponse containing matching entities and pagination metadata
     *
     * @example
     * ```typescript
     * this.productVariationService.search$(filterOptions, 0, 20).subscribe(response => {
     *   console.log(`Found ${response.total} product variations`);
     * });
     * ```
     */
    search$(
        filterOptions: ProductVariationSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ProductVariation>> {

        const request: SearchRequest<ProductVariationSearchFilterOptions> = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            ProductVariation.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$<ProductVariation, ProductVariationSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Retrieves multiple ProductVariation entities in a single bulk request.
     * More efficient than making individual get() calls when fetching multiple entities.
     *
     * @param ids - Array of unique identifiers for the ProductVariations to retrieve
     * @returns Observable that emits a BulkResponse containing all requested entities
     *
     * @example
     * ```typescript
     * this.productVariationService.bulkGet$(['id-1', 'id-2']).subscribe(response => {
     *   console.log('Bulk response:', response);
     * });
     * ```
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<ProductVariation>(id, ProductVariation.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieves a single ProductVariation entity by its unique identifier.
     *
     * @param id - The unique identifier of the ProductVariation to retrieve
     * @returns Observable that emits the requested ProductVariation entity
     *
     * @example
     * ```typescript
     * this.productVariationService.get$('variation-123').subscribe(variation => {
     *   console.log(variation.name);
     * });
     * ```
     */
    get$(id: string): Observable<ProductVariation> {
        const request = RequestUtil.createGetRequest<void>(id, ProductVariation.modelName, ModelAction.ClientGet);
        return this._internalApiRequestService.get$<ProductVariation>(request, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Retrieves all ProductVariation entities matching the specified filter criteria.
     * Results are returned as a complete array without pagination.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [sort] - Optional sorting configuration
     * @returns Observable that emits an array of all matching ProductVariation entities
     *
     * @example
     * ```typescript
     * this.productVariationService.getAll$(filterOptions).subscribe(variations => {
     *   console.log(`Found ${variations.length} product variations`);
     * });
     * ```
     */
    getAll$(filterOptions: ProductVariationSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<ProductVariation>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, ProductVariation.modelName, undefined, undefined, filterOptions, sort
        );
        return this._internalApiRequestService.getAll$<ProductVariation, ProductVariationSearchFilterOptions>(request, this.basePath);
    }
}
